import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  mediaApi,
  groesseLesbar,
  FASSUNG_LABELS,
  FASSUNG_STATUS_LABELS,
  type FassungStatus,
  type MediaAsset,
  type MediaFassung,
} from '../../services/mediaService';
import { blobSpeichern } from '../../services/download';

/** Solange etwas aussteht, so oft neu laden. */
export const NACHLADEN_MS = 15_000;

const BADGE: Record<FassungStatus, string> = {
  ausstehend: 'badge badge-warning',
  ok: 'badge badge-success',
  fehler: 'badge badge-error',
  nicht_noetig: 'badge badge-neutral',
};

/** 75.4 -> "1:15" */
export function dauerLesbar(sekunden: number | null | undefined): string {
  if (sekunden == null || !Number.isFinite(sekunden)) return '–';
  const s = Math.round(sekunden);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Dateiname zum Speichern: Name des Originals ohne Endung, dazu Art und Seite. */
export function fassungDateiname(originalName: string, f: Pick<MediaFassung, 'art' | 'seite' | 'mimeType'>): string {
  const basis = originalName.replace(/\.[A-Za-z0-9]{1,5}$/, '') || 'medium';
  const endung = f.mimeType === 'image/jpeg' ? 'jpg' : 'mp4';
  return `${basis}-${f.art}${f.seite > 0 ? `-${f.seite}` : ''}.${endung}`;
}

const STATUS_HILFE: Partial<Record<FassungStatus, string>> = {
  ausstehend: 'Wird im Hintergrund erzeugt (eine Datei zur Zeit, Videos dauern einige Minuten).',
  nicht_noetig: 'Das Original passt schon; beim Veröffentlichen geht es unverändert raus.',
};

/**
 * Plattform-Fassungen eines Mediums (Vorschau in der Medienbibliothek):
 * Stand je Fassung, Groesse, Abmessung, Dauer, Download und "neu erzeugen".
 * Laedt alle 15 s nach, solange etwas aussteht.
 */
export const MediaFassungen: React.FC<{ asset: MediaAsset; darfBearbeiten?: boolean }> = ({ asset, darfBearbeiten = false }) => {
  const [fassungen, setFassungen] = useState<MediaFassung[] | null>(null);
  const [wartet, setWartet] = useState(false);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const aktiv = useRef(true);

  const laden = useCallback(async () => {
    try {
      const antwort = await mediaApi.fassungen(asset.id);
      if (!aktiv.current) return;
      setFassungen(antwort.fassungen);
      setWartet(antwort.wartet);
      setFehler('');
    } catch (err: any) {
      if (aktiv.current) setFehler(err?.response?.data?.error || 'Die Fassungen konnten nicht geladen werden.');
    }
  }, [asset.id]);

  useEffect(() => {
    aktiv.current = true;
    laden();
    return () => { aktiv.current = false; };
  }, [laden]);

  const offen = wartet || (fassungen !== null && (fassungen.length === 0 || fassungen.some((f) => f.status === 'ausstehend')));
  useEffect(() => {
    if (!offen) return undefined;
    const uhr = setTimeout(() => { laden(); }, NACHLADEN_MS);
    return () => clearTimeout(uhr);
  }, [offen, fassungen, laden]);

  const neuErzeugen = async () => {
    setLaeuft(true);
    try {
      const antwort = await mediaApi.fassungenNeu(asset.id);
      setFassungen(antwort.fassungen);
      setWartet(antwort.wartet);
      setFehler('');
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Die Fassungen konnten nicht neu erzeugt werden.');
    } finally {
      setLaeuft(false);
    }
  };

  const speichern = async (f: MediaFassung) => {
    try {
      blobSpeichern(await mediaApi.fassungInhalt(asset.id, f.id, true), fassungDateiname(asset.originalName, f));
    } catch {
      setFehler('Der Download der Fassung ist fehlgeschlagen.');
    }
  };

  return (
    <section className="mt-4" aria-label="Fassungen">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 className="text-base" style={{ margin: 0 }}>Fassungen</h3>
        {wartet && <span className="badge badge-warning">wird erzeugt …</span>}
        {darfBearbeiten && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={neuErzeugen}
            disabled={laeuft}
            title="Alle Fassungen aus dem Original neu erzeugen"
          >
            {laeuft ? 'Wird vorgemerkt …' : 'Neu erzeugen'}
          </button>
        )}
      </div>
      <p className="text-sm text-muted" style={{ margin: '4px 0 8px' }}>
        Beim Veröffentlichen nimmt Intermal automatisch die passende Fassung (LinkedIn, Instagram, X).
      </p>
      {fehler && <div className="alert alert-error">{fehler}</div>}
      {fassungen === null && !fehler && <p className="text-muted text-sm">Fassungen werden geladen …</p>}
      {fassungen !== null && fassungen.length === 0 && (
        <p className="text-muted text-sm">Noch keine Fassungen – sie entstehen nach dem Hochladen im Hintergrund.</p>
      )}
      {fassungen !== null && fassungen.length > 0 && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Fassung</th>
                <th>Status</th>
                <th>Größe</th>
                <th>Abmessung</th>
                <th>Dauer</th>
                <th className="text-center">Datei</th>
              </tr>
            </thead>
            <tbody>
              {fassungen.map((f) => (
                <tr key={f.id}>
                  <td>
                    {FASSUNG_LABELS[f.art] ?? f.art}
                    {f.seite > 0 && <span className="text-muted"> · Seite {f.seite}</span>}
                  </td>
                  <td>
                    <span className={BADGE[f.status] ?? 'badge'} title={f.status === 'fehler' ? (f.fehler || undefined) : STATUS_HILFE[f.status]}>
                      {FASSUNG_STATUS_LABELS[f.status] ?? f.status}
                    </span>
                    {f.status === 'fehler' && f.fehler && <div className="text-xs text-muted">{f.fehler}</div>}
                  </td>
                  <td>{f.sizeBytes != null ? groesseLesbar(f.sizeBytes) : '–'}</td>
                  <td>{f.breite && f.hoehe ? `${f.breite} × ${f.hoehe}` : '–'}</td>
                  <td>{f.mimeType === 'image/jpeg' ? '–' : dauerLesbar(f.dauerSek)}</td>
                  <td className="text-center">
                    {f.status === 'ok' && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => speichern(f)}
                        aria-label={`${FASSUNG_LABELS[f.art] ?? f.art}${f.seite > 0 ? ` Seite ${f.seite}` : ''} herunterladen`}
                      >
                        Herunterladen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default MediaFassungen;
